import { Loader2 } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { trackBeginCheckout } from "@/lib/dataLayer";
import { getStoredGclid } from "@/lib/attribution";
import { getReferralId } from "@/lib/referral";
import { savePendingCheckout } from "@/lib/pendingCheckout";
import {
  originatingProjectId,
  saveCheckoutReturn,
} from "@/lib/checkoutReturn";
import { getBasePlan } from "@/lib/pricingPlans";
import { cn } from "@/lib/utils";

/**
 * "Pro would have saved you $25 in the last 30 days", shown above the pack
 * grids to people whose last 30 days of credit packs would have cost less
 * on a monthly plan (or, for Plus / Creator subscribers, on the plan above).
 * The server decides who qualifies and computes every number
 * (server/planSavings.ts); this renders it and starts the checkout, or the
 * in-place switch for a web subscriber.
 */

export type PlanSavingsSource =
  | "upgrade_modal"
  | "subscription_page"
  | "credit_history";

const ENGLISH_PLAN_NAMES: Record<string, string> = {
  plus: "Plus",
  creator: "Creator",
  pro_v2: "Pro",
};

/** $25, $1,342, $24.50 */
export function formatUsdCents(cents: number): string {
  const digits = cents % 100 === 0 ? 0 : 2;
  return `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

function planName(
  t: ReturnType<typeof useTranslation>["t"],
  planId: string,
): string {
  const base = getBasePlan(planId);
  return t(`pricing.${base === "pro_v2" ? "pro" : base}`, {
    defaultValue: ENGLISH_PLAN_NAMES[base] ?? base,
  });
}

export default function PlanSavingsNudge({
  source,
  onSwitched,
  className,
  fallback = null,
}: {
  source: PlanSavingsSource;
  /** Called after a web subscriber's plan is switched in place. */
  onSwitched?: () => void;
  className?: string;
  /** Rendered instead when there is no saving to show. */
  fallback?: ReactNode;
}) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const utils = trpc.useUtils();
  const { data: savings, isLoading } = trpc.payment.getPlanSavings.useQuery(
    undefined,
    {
      enabled: isAuthenticated,
      staleTime: 5 * 60 * 1000,
    },
  );

  const checkout = trpc.payment.createSubscriptionCheckout.useMutation({
    onSuccess: (data, variables) => {
      if (data.switched) {
        utils.payment.getSubscription.invalidate();
        utils.payment.getPlanSavings.invalidate();
        toast.success(
          t("subscription.planSwitched", "Your plan has been updated."),
        );
        onSwitched?.();
        return;
      }
      saveCheckoutReturn(data, variables);
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      toast.error(error.message || t("pricing.checkoutFailed"));
    },
  });

  const analytics = savings
    ? {
        source,
        plan: savings.planId,
        current_plan: savings.currentPlanId ?? "free",
        savings_usd: savings.savingsCents / 100,
        paid_usd: savings.paidCents / 100,
        plan_cost_usd: savings.planCostCents / 100,
      }
    : null;

  const shownKey = savings ? `${savings.planId}:${savings.savingsCents}` : null;
  const lastShownKey = useRef<string | null>(null);
  useEffect(() => {
    if (!shownKey || !analytics || lastShownKey.current === shownKey) return;
    lastShownKey.current = shownKey;
    posthog.capture("plan_savings_nudge_shown", analytics);
  }, [shownKey]);

  // Nothing while loading, so the fallback doesn't flash before the line.
  if (isLoading) return null;
  if (!savings) return <>{fallback}</>;

  const suggested = planName(t, savings.planId);
  const planPriceCents = savings.planCostCents - savings.topUpCents;
  const paidLine = savings.currentPlanId
    ? t("pricing.planSavingsPaidPlanAndPacks", {
        plan: planName(t, savings.currentPlanId),
        amount: formatUsdCents(savings.paidCents),
      })
    : t("pricing.planSavingsPaidPacks", {
        amount: formatUsdCents(savings.paidCents),
      });
  const costLine =
    savings.topUpCents > 0
      ? t("pricing.planSavingsCostWithTopUps", {
          plan: suggested,
          amount: formatUsdCents(savings.planCostCents),
        })
      : t("pricing.planSavingsCostPlan", {
          plan: suggested,
          amount: formatUsdCents(savings.planCostCents),
        });

  const handleClick = () => {
    posthog.capture("plan_savings_nudge_clicked", analytics ?? {});
    const price = planPriceCents / 100;
    const englishName =
      ENGLISH_PLAN_NAMES[getBasePlan(savings.planId)] ?? savings.planId;
    trackBeginCheckout(
      [
        {
          item_id: savings.planId,
          item_name: englishName,
          price,
          quantity: 1,
          item_category: "subscription",
          item_variant: "monthly",
        },
      ],
      price,
      "USD",
      { source: "plan_savings_nudge", trigger: source },
    );
    // A web subscriber is switched in place; only a new subscription goes
    // through a Stripe checkout page the user could abandon.
    if (!savings.currentPlanId) {
      savePendingCheckout({
        kind: "subscription",
        planId: savings.planId,
        planName: `${englishName} Monthly`,
        price,
      });
    }
    checkout.mutate({
      planId: savings.planId,
      gclid: getStoredGclid() ?? undefined,
      clientReferenceId: getReferralId(),
      returnProjectId: originatingProjectId(),
    });
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 sm:flex-row sm:items-center",
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">
          {t("pricing.planSavingsHeadline", {
            plan: suggested,
            amount: formatUsdCents(savings.savingsCents),
          })}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {paidLine} · {costLine}
        </p>
      </div>
      <Button
        onClick={handleClick}
        disabled={checkout.isPending}
        size="sm"
        className="subscription-cta shrink-0 rounded-full px-5 font-semibold"
      >
        {checkout.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          t("pricing.getPlanCta", { plan: suggested })
        )}
      </Button>
    </div>
  );
}
